import {
  listItemButtonClasses,
  listItemClasses,
  listItemIconClasses,
  listItemTextClasses,
  listSubheaderClasses,
  type Components,
} from '@mui/material';
import { themeVars } from '../themeVars';
import { typographyStyles, type Theme } from './utils';

export const MuiList: Components<Theme>['MuiList'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      paddingBlock: 0,

      [`.${listItemClasses.root}:has(& + .${listSubheaderClasses.root})`]: {
        marginBottom: theme.spacing(3),
      },
    }),
  },
};

const listItemHeight = 48;
export const listItemPadding = 24;

export const MuiListItem: Components<Theme>['MuiListItem'] = {
  styleOverrides: {
    root: {
      minHeight: listItemHeight,
    },
  },
  variants: [
    {
      props: ({ ownerState }) => !ownerState.disablePadding && !ownerState.disableGutters,
      style: {
        paddingLeft: listItemPadding,
        paddingRight: listItemPadding,
      },
    },
    {
      props: ({ ownerState }) => !!ownerState.divider,
      style: ({ theme }) => ({
        borderColor: themeVars(theme).palette.border.subtle,
      }),
    },
  ],
};

export const MuiListItemIcon: Components<Theme>['MuiListItemIcon'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      marginRight: theme.spacing(1),
      color: themeVars(theme).palette.text.secondary,
      minWidth: 'auto',
    }),
  },
};

export const MuiListItemText: Components<Theme>['MuiListItemText'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      [`.${listItemTextClasses.secondary}`]: {
        marginTop: theme.spacing(0.5),
      },
    }),
  },
};

export const MuiListItemButton: Components<Theme>['MuiListItemButton'] = {
  styleOverrides: {
    root: ({ theme }) => {
      const palette = themeVars(theme).palette;
      return {
        minHeight: listItemHeight,
        '&:hover': {
          textDecoration: 'none',
          backgroundColor: themeVars(theme).palette.action.hover,
          // Reset on touch devices, it doesn't add specificity
          '@media (hover: none)': {
            backgroundColor: 'transparent',
          },
        },
        [`&.${listItemButtonClasses.selected}`]: {
          backgroundColor: palette.action.selected,

          [`&.${listItemButtonClasses.focusVisible}`]: {
            backgroundColor: palette.action.selected,
            boxShadow: `inset 0 0 0 1px ${themeVars(theme).palette.border.focused}, inset 0 0 0 2px ${themeVars(theme).palette.background.default}`,
          },

          [`& .${listItemIconClasses.root}`]: {
            color: themeVars(theme).palette.text.primary,
          },
        },
        [`&.${listItemButtonClasses.selected}:hover`]: {
          backgroundColor: palette.action.selected,
          // Reset on touch devices, it doesn't add specificity
          '@media (hover: none)': {
            backgroundColor: palette.action.selected,
          },
        },
        [`&.${listItemButtonClasses.selected}:active`]: {
          backgroundColor: palette.action.pressed,
        },
        [`&.${listItemButtonClasses.focusVisible}`]: {
          boxShadow: `inset 0 0 0 1px ${themeVars(theme).palette.border.focused}`,
          background: 'none',
        },
        [`&.${listItemButtonClasses.disabled}`]: {
          opacity: 1,
          '& > *': {
            // Don't apply this opacity to the divider
            opacity: themeVars(theme).palette.action.disabledOpacity,
          },
        },
      };
    },
  },
  variants: [
    {
      props: ({ ownerState }) => !!ownerState.divider,
      style: ({ theme }) => ({
        borderColor: themeVars(theme).palette.border.subtle,
      }),
    },
    {
      props: ({ ownerState }) => !ownerState.disableGutters,
      style: {
        paddingLeft: listItemPadding,
        paddingRight: listItemPadding,
      },
    },
  ],
};

export const MuiListSubheader: Components<Theme>['MuiListSubheader'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      ...typographyStyles(theme.typography.overline),
      background: themeVars(theme).palette.background.default,
      margin: 0,
      paddingInline: theme.spacing(1),
    }),
  },
};
